/**
 * Results Dashboard Component
 *
 * Main dashboard page for viewing simulation results with
 * summary cards, charts, variable browser, and data table.
 */

import { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSimulationStore } from '@/simulation/simulationStore';
import { useResultsStore } from '../resultsStore';
import { SummaryCards } from './SummaryCards';
import { EndUseChart } from './EndUseChart';
import { TimeSeriesChart, SERIES_COLORS } from './TimeSeriesChart';
import { VariableBrowser } from './VariableBrowser';
import { DataTable } from './DataTable';
import { FileRequiredEmptyState } from '@/components/FileRequiredEmptyState';
import { SimulationHistoryPanel } from './SimulationHistoryPanel';
import type { OutputVariable } from '@/simulation/parsers/types';
import { openPrintPreview } from '../utils/export';
import { getUnitCategory, getUniqueUnitCategories } from '../utils/unitConversion';

/**
 * View mode tabs
 */
const VIEW_MODES = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'table', label: 'Data Table' },
  { id: 'compare', label: 'Compare' },
] as const;

/**
 * Unit toggle button component
 */
function UnitToggle() {
  const unitSystem = useResultsStore((s) => s.unitSystem);
  const toggleUnitSystem = useResultsStore((s) => s.toggleUnitSystem);

  return (
    <button
      onClick={toggleUnitSystem}
      className="flex items-center gap-1 px-3 py-1.5 bg-secondary text-muted-foreground rounded hover:bg-muted transition-colors text-sm"
      title={`Toggle unit system (currently ${unitSystem})`}
      aria-label={`Unit system: ${unitSystem === 'SI' ? 'Metric' : 'Imperial'}. Click to switch.`}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
      {unitSystem}
    </button>
  );
}

/**
 * Header actions dropdown for secondary actions
 */
function HeaderActionsDropdown({ onPrint }: { onPrint: () => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 bg-secondary text-muted-foreground rounded hover:bg-muted transition-colors"
        aria-label="More actions"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-lg z-50 py-1"
          role="menu"
          aria-orientation="vertical"
        >
          <button
            onClick={() => { onPrint(); setIsOpen(false); }}
            className="w-full px-4 py-2 text-sm text-left text-foreground hover:bg-muted transition-colors flex items-center gap-2"
            role="menuitem"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
              />
            </svg>
            Print Dashboard
          </button>
          <div className="border-t border-border my-1" />
          <div className="px-4 py-2 text-xs text-muted-foreground">
            <p className="font-medium mb-1">Keyboard Shortcuts</p>
            <div className="space-y-0.5">
              <p><kbd className="px-1 bg-muted rounded text-[10px]">U</kbd> Toggle units</p>
              <p><kbd className="px-1 bg-muted rounded text-[10px]">1-3</kbd> Switch view</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * No results placeholder with simulation history
 */
function NoResults({ onLoadFromHistory }: { onLoadFromHistory?: () => void }) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center h-full p-6 gap-8">
      <FileRequiredEmptyState
        reason="no-file"
        title="No Results Available"
        message="Run a simulation to view results here, or load from history."
        action={{
          label: 'Go to Simulation',
          onClick: () => navigate('/simulate'),
        }}
      />

      {/* Simulation History Panel */}
      <div className="w-full max-w-2xl">
        <SimulationHistoryPanel onLoadResults={onLoadFromHistory} />
      </div>
    </div>
  );
}

/**
 * Maximum number of different unit types allowed on a single chart
 */
const MAX_UNIT_TYPES = 2;

/**
 * Custom chart builder component with accessibility support
 */
function ChartBuilder({
  availableVariables,
  onClose,
}: {
  availableVariables: OutputVariable[];
  onClose: () => void;
}) {
  const selectedVariables = useResultsStore((s) => s.selectedVariables);
  const setSelectedVariables = useResultsStore((s) => s.setSelectedVariables);
  const output = useSimulationStore((s) => s.output);
  const unitSystem = useResultsStore((s) => s.unitSystem);
  const defaultFrequency = useResultsStore((s) => s.defaultFrequency);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Focus trap and Escape key handling
  useEffect(() => {
    // Focus the close button when modal opens
    closeButtonRef.current?.focus();

    // Handle Escape key
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }

      // Focus trap
      if (event.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Get unique unit categories from selected variables
  const selectedUnitCategories = useMemo(() => {
    const units = selectedVariables.map((v) => v.units);
    return getUniqueUnitCategories(units);
  }, [selectedVariables]);

  // Determine which variables can be selected (unit category must be one of the existing ones if at max)
  const disabledVariableIds = useMemo(() => {
    if (selectedUnitCategories.length < MAX_UNIT_TYPES) {
      return new Set<number>();
    }

    // Disable variables that would introduce a third unit type
    const allowedCategories = new Set(selectedUnitCategories);
    const disabled = new Set<number>();

    for (const v of availableVariables) {
      const category = getUnitCategory(v.units);
      if (!allowedCategories.has(category)) {
        disabled.add(v.id);
      }
    }

    return disabled;
  }, [availableVariables, selectedUnitCategories]);

  // Build series from selected variables
  const series = useMemo(() => {
    if (!output?.eso) return [];

    return selectedVariables.map((v, i) => {
      const timeSeries = output.eso?.timeSeries.get(v.id);
      const color = SERIES_COLORS[i % SERIES_COLORS.length] ?? '#3b82f6';
      return {
        id: `var-${String(v.id)}`,
        timeSeries,
        name: v.keyValue ? `${v.keyValue}: ${v.variableName}` : v.variableName,
        color,
        visible: true,
      };
    }).filter((s): s is typeof s & { timeSeries: NonNullable<typeof s.timeSeries> } => s.timeSeries !== undefined);
  }, [selectedVariables, output]);

  const handleSelectionChange = (variables: OutputVariable[]) => {
    // Filter out variables that would exceed the max unit types
    const units = variables.map((v) => v.units);
    const categories = getUniqueUnitCategories(units);

    if (categories.length > MAX_UNIT_TYPES) {
      // Don't update - keep the previous selection
      return;
    }

    setSelectedVariables(variables);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="chart-builder-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div ref={modalRef} className="bg-card rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h2 id="chart-builder-title" className="text-xl font-semibold text-foreground">Custom Chart Builder</h2>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded hover:bg-muted"
            aria-label="Close chart builder"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex gap-4 p-4 overflow-hidden min-h-0">
          {/* Variable browser */}
          <div className="w-80 shrink-0">
            <VariableBrowser
              variables={availableVariables}
              selectedIds={selectedVariables.map((v) => v.id)}
              disabledIds={disabledVariableIds}
              onSelectionChange={handleSelectionChange}
              multiSelect
              maxHeight={500}
              className="h-full"
              {...(selectedUnitCategories.length >= MAX_UNIT_TYPES
                ? { warningMessage: `Maximum ${MAX_UNIT_TYPES} unit types per chart. Deselect a variable to add different units.` }
                : {})}
            />
          </div>

          {/* Chart preview */}
          <div className="flex-1 min-w-0">
            {series.length > 0 ? (
              <TimeSeriesChart
                series={series}
                unitSystem={unitSystem}
                initialFrequency={defaultFrequency}
                title="Custom Chart Preview"
                height={450}
              />
            ) : (
              <div className="h-full bg-muted/30 border-2 border-dashed border-muted rounded-lg flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <svg
                    className="w-12 h-12 mx-auto mb-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
                    />
                  </svg>
                  <p className="font-medium">Chart Preview</p>
                  <p className="text-sm mt-1">Select variables from the browser on the left</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Results Dashboard Component
 */
export function ResultsDashboard() {
  const output = useSimulationStore((s) => s.output);
  const status = useSimulationStore((s) => s.status);
  const availableVariables = useSimulationStore((s) => s.availableVariables);
  const viewMode = useResultsStore((s) => s.viewMode);
  const setViewMode = useResultsStore((s) => s.setViewMode);
  const unitSystem = useResultsStore((s) => s.unitSystem);
  const toggleUnitSystem = useResultsStore((s) => s.toggleUnitSystem);
  const buildingArea = useResultsStore((s) => s.buildingArea);
  const setBuildingArea = useResultsStore((s) => s.setBuildingArea);
  const selectedVariables = useResultsStore((s) => s.selectedVariables);
  const selectVariable = useResultsStore((s) => s.selectVariable);
  const defaultFrequency = useResultsStore((s) => s.defaultFrequency);

  const dashboardRef = useRef<HTMLDivElement>(null);
  const [showChartBuilder, setShowChartBuilder] = useState(false);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      // Ignore if user is typing in an input
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return;
      }

      switch (event.key.toLowerCase()) {
        case 'u':
          toggleUnitSystem();
          break;
        case '1':
          setViewMode('dashboard');
          break;
        case '2':
          setViewMode('table');
          break;
        case '3':
          setViewMode('compare');
          break;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleUnitSystem, setViewMode]);

  // Debug: log what we're receiving from the store
  console.log('[ResultsDashboard] Render:', {
    status,
    hasOutput: !!output,
    hasEso: !!output?.eso,
    variablesIsMap: output?.eso?.variables instanceof Map,
    timeSeriesIsMap: output?.eso?.timeSeries instanceof Map,
    availableVariablesCount: availableVariables.length,
  });

  // Check if we have results - use both status and output.eso for reliability
  const hasResults = status === 'complete' && output?.eso !== undefined;

  // Build series for selected variables chart
  const selectedSeries = useMemo(() => {
    if (!output?.eso || selectedVariables.length === 0) return [];

    return selectedVariables.map((v, i) => {
      const timeSeries = output.eso?.timeSeries.get(v.id);
      const color = SERIES_COLORS[i % SERIES_COLORS.length] ?? '#3b82f6';
      return {
        id: `var-${String(v.id)}`,
        timeSeries,
        name: v.keyValue ? `${v.keyValue}: ${v.variableName}` : v.variableName,
        color,
        visible: true,
      };
    }).filter((s): s is typeof s & { timeSeries: NonNullable<typeof s.timeSeries> } => s.timeSeries !== undefined);
  }, [selectedVariables, output]);

  // Build series for data table
  const tableSeries = useMemo(() => {
    if (!output?.eso || selectedVariables.length === 0) return [];

    return selectedVariables.map((v) => {
      const timeSeries = output.eso?.timeSeries.get(v.id);
      return {
        id: `var-${String(v.id)}`,
        name: v.keyValue ? `${v.keyValue}: ${v.variableName}` : v.variableName,
        timeSeries,
      };
    }).filter((s): s is typeof s & { timeSeries: NonNullable<typeof s.timeSeries> } => s.timeSeries !== undefined);
  }, [selectedVariables, output]);

  // Handle print
  const handlePrint = () => {
    if (dashboardRef.current) {
      openPrintPreview(dashboardRef.current);
    }
  };

  // Handle variable selection from browser
  const handleVariableSelect = useCallback(
    (variable: OutputVariable) => {
      selectVariable(variable);
    },
    [selectVariable]
  );

  if (!hasResults) {
    return (
      <div className="flex flex-col h-full bg-background">
        <NoResults onLoadFromHistory={() => {
          // Callback after results are loaded from history
          // The store update from setComplete should trigger a re-render automatically
          // Log state for debugging
          const state = useSimulationStore.getState();
          console.log('[ResultsDashboard] onLoadFromHistory callback:', {
            hasOutput: !!state.output,
            hasEso: !!state.output?.eso,
            variablesIsMap: state.output?.eso?.variables instanceof Map,
            availableVariablesCount: state.availableVariables.length,
          });
        }} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Skip link for accessibility */}
      <a
        href="#results-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded"
      >
        Skip to results content
      </a>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-border gap-3 sm:gap-4">
        <div className="flex-shrink-0">
          <h1 className="text-lg sm:text-xl font-bold text-foreground">Results Dashboard</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1">
            {availableVariables.length} output variables available
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto justify-end">
          {/* Building area input - hidden on mobile, shown on larger screens */}
          <div className="hidden md:flex items-center gap-2">
            <label htmlFor="building-area" className="text-sm text-muted-foreground whitespace-nowrap">
              Area ({unitSystem === 'SI' ? 'm²' : 'ft²'}):
            </label>
            <input
              id="building-area"
              type="number"
              value={buildingArea || ''}
              onChange={(e) => { setBuildingArea(parseFloat(e.target.value) || 0); }}
              placeholder="0"
              className="w-20 px-2 py-1 text-sm bg-secondary border border-border rounded text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              aria-describedby="building-area-hint"
            />
            <span id="building-area-hint" className="sr-only">Enter building floor area for EUI calculation</span>
          </div>

          {/* Unit toggle */}
          <UnitToggle />

          {/* View mode tabs */}
          <div className="flex bg-secondary rounded-lg p-0.5 sm:p-1" role="tablist" aria-label="View mode">
            {VIEW_MODES.map((mode, index) => (
              <button
                key={mode.id}
                role="tab"
                aria-selected={viewMode === mode.id}
                aria-controls={`${mode.id}-panel`}
                onClick={() => { setViewMode(mode.id); }}
                className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md transition-colors ${
                  viewMode === mode.id
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                title={`${mode.label} (Press ${index + 1})`}
              >
                {mode.label}
              </button>
            ))}
          </div>

          {/* More actions dropdown */}
          <HeaderActionsDropdown onPrint={handlePrint} />
        </div>
      </div>

      {/* Content */}
      <div id="results-content" ref={dashboardRef} className="flex-1 overflow-auto p-4 sm:p-6" role="tabpanel">
        {viewMode === 'dashboard' && output.eso && (
          <div className="space-y-4 sm:space-y-6">
            {/* Summary Cards */}
            <SummaryCards
              data={output.eso}
              buildingArea={buildingArea}
              unitSystem={unitSystem}
            />

            {/* Main content area - Variable Browser on left when no selection */}
            {selectedSeries.length === 0 ? (
              /* Getting Started Layout - Browser prominent */
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                {/* Variable Browser - takes priority */}
                <div className="lg:col-span-1 order-1 lg:order-1">
                  <div className="bg-blue-500/10 border-2 border-blue-500/30 rounded-lg p-1">
                    <VariableBrowser
                      variables={availableVariables}
                      selectedIds={selectedVariables.map((v) => v.id)}
                      onSelect={handleVariableSelect}
                      multiSelect
                      maxHeight={450}
                    />
                  </div>
                  <p className="text-xs text-blue-400 mt-2 text-center flex items-center justify-center gap-1">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Select variables to visualize data
                  </p>
                </div>

                {/* Charts column */}
                <div className="lg:col-span-2 order-2 lg:order-2 space-y-4 sm:space-y-6">
                  {/* End Use Chart */}
                  <EndUseChart
                    data={output.eso}
                    unitSystem={unitSystem}
                    height={280}
                  />

                  {/* Empty chart placeholder with arrow pointing to browser */}
                  <div className="bg-card rounded-lg border-2 border-dashed border-muted p-6 flex flex-col items-center justify-center" style={{ height: 240 }}>
                    <div className="flex items-center gap-4 text-muted-foreground">
                      <svg
                        className="w-8 h-8 hidden lg:block transform rotate-180"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                      <div className="text-center">
                        <svg
                          className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          />
                        </svg>
                        <p className="font-medium text-foreground">Custom Time Series Chart</p>
                        <p className="text-sm mt-1">Select variables from the browser to create charts</p>
                      </div>
                    </div>
                    <button
                      onClick={() => { setShowChartBuilder(true); }}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      Open Chart Builder
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Data Selected Layout - Charts prominent */
              <>
                {/* Charts row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* End Use Chart */}
                  <EndUseChart
                    data={output.eso}
                    unitSystem={unitSystem}
                    height={300}
                  />

                  {/* Selected Variables Chart */}
                  <TimeSeriesChart
                    series={selectedSeries}
                    unitSystem={unitSystem}
                    initialFrequency={defaultFrequency}
                    title="Selected Variables"
                    height={300}
                  />
                </div>

                {/* Variable Browser + Data Table */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                  <div className="lg:col-span-1">
                    <VariableBrowser
                      variables={availableVariables}
                      selectedIds={selectedVariables.map((v) => v.id)}
                      onSelect={handleVariableSelect}
                      multiSelect
                      maxHeight={400}
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <DataTable
                      series={tableSeries}
                      unitSystem={unitSystem}
                      height={450}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {viewMode === 'table' && output.eso && (
          <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 h-full min-h-[500px]">
            {/* Variable Browser sidebar */}
            <div className={`w-full lg:w-72 xl:w-80 shrink-0 ${tableSeries.length === 0 ? 'lg:order-1' : ''}`}>
              <div className={tableSeries.length === 0 ? 'bg-blue-500/10 border-2 border-blue-500/30 rounded-lg p-1' : ''}>
                <VariableBrowser
                  variables={availableVariables}
                  selectedIds={selectedVariables.map((v) => v.id)}
                  onSelect={handleVariableSelect}
                  multiSelect
                  maxHeight={550}
                />
              </div>
              {tableSeries.length === 0 && (
                <p className="text-xs text-blue-400 mt-2 text-center flex items-center justify-center gap-1">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  Select variables to view data
                </p>
              )}
            </div>

            {/* Data Table */}
            <div className={`flex-1 min-w-0 ${tableSeries.length === 0 ? 'lg:order-2' : ''}`}>
              {tableSeries.length > 0 ? (
                <DataTable
                  series={tableSeries}
                  unitSystem={unitSystem}
                  height={550}
                />
              ) : (
                <div className="bg-card rounded-lg border-2 border-dashed border-muted h-full min-h-[400px] flex items-center justify-center">
                  <div className="text-center text-muted-foreground p-6">
                    <div className="flex items-center justify-center gap-4">
                      <svg
                        className="w-8 h-8 hidden lg:block transform rotate-180"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                      <div>
                        <svg
                          className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="font-medium text-foreground">Data Table</p>
                        <p className="text-sm mt-1">Select variables from the browser to view tabular data</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {viewMode === 'compare' && output.eso && (
          <div className="space-y-4 sm:space-y-6">
            {selectedSeries.length === 0 ? (
              /* Getting Started Layout for Compare */
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                {/* Variable Browser - prominent when no selection */}
                <div className="lg:col-span-1 order-1">
                  <div className="bg-blue-500/10 border-2 border-blue-500/30 rounded-lg p-1">
                    <VariableBrowser
                      variables={availableVariables}
                      selectedIds={selectedVariables.map((v) => v.id)}
                      onSelect={handleVariableSelect}
                      multiSelect
                      maxHeight={500}
                    />
                  </div>
                  <p className="text-xs text-blue-400 mt-2 text-center flex items-center justify-center gap-1">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Select variables to compare
                  </p>
                </div>

                {/* Empty comparison chart */}
                <div className="lg:col-span-2 order-2">
                  <div className="bg-card rounded-lg border-2 border-dashed border-muted p-8 text-center" style={{ minHeight: 400 }}>
                    <div className="flex items-center justify-center gap-4">
                      <svg
                        className="w-8 h-8 hidden lg:block transform rotate-180"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                      <div>
                        <svg
                          className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          />
                        </svg>
                        <h3 className="text-lg font-medium text-foreground mb-2">Variable Comparison</h3>
                        <p className="text-muted-foreground mb-4">
                          Select variables from the browser to compare their values over time
                        </p>
                        <button
                          onClick={() => { setShowChartBuilder(true); }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Open Chart Builder
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Data Selected Layout for Compare */
              <>
                {/* Chart builder button */}
                <div className="flex justify-end">
                  <button
                    onClick={() => { setShowChartBuilder(true); }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    Edit Chart
                  </button>
                </div>

                {/* Selected Variables Chart */}
                <TimeSeriesChart
                  series={selectedSeries}
                  unitSystem={unitSystem}
                  initialFrequency={defaultFrequency}
                  title="Variable Comparison"
                  height={400}
                  showBrush
                />

                {/* Variable Browser */}
                <VariableBrowser
                  variables={availableVariables}
                  selectedIds={selectedVariables.map((v) => v.id)}
                  onSelect={handleVariableSelect}
                  multiSelect
                  maxHeight={300}
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* Chart Builder Modal */}
      {showChartBuilder && (
        <ChartBuilder
          availableVariables={availableVariables}
          onClose={() => { setShowChartBuilder(false); }}
        />
      )}
    </div>
  );
}

export default ResultsDashboard;
